import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";

const PAGE_OPTIONS = [
  { id: "*", label: "All pages" },
  { id: "home", label: "Home" },
  { id: "shop", label: "Shop (all categories)" },
  { id: "jewelry", label: "Shop · Jewelry" },
  { id: "ai-prompts", label: "Shop · AI Prompts" },
  { id: "ebooks", label: "Shop · Ebooks & Cookbooks" },
  { id: "remedies", label: "Shop · Remedies" },
  { id: "merch", label: "Shop · Merch" },
  { id: "home-goods", label: "Shop · Home goods" },
  { id: "media", label: "Shop · Media" },
  { id: "product", label: "Product detail pages" },
  { id: "about", label: "About" },
  { id: "wishlist", label: "Wishlist" },
  { id: "account", label: "Account" },
];

const EMPTY = {
  enabled: true,
  image: "",
  eyebrow: "",
  title: "",
  subtitle: "",
  cta_label: "",
  cta_link: "",
  align: "left",
  pages: ["home"],
  sort_order: 0,
};

export function BannersPanel() {
  const [banners, setBanners] = useState([]);
  const [editing, setEditing] = useState(null); // "new" | id | null
  const [form, setForm] = useState(EMPTY);

  async function load() {
    try {
      const { data } = await api.get("/promo-banners/all");
      setBanners(data || []);
    } catch (e) {
      toast.error("Could not load banners.");
    }
  }

  useEffect(() => { load(); }, []);

  function openNew() { setForm(EMPTY); setEditing("new"); }
  function openEdit(b) { setForm({ ...EMPTY, ...b }); setEditing(b.id); }

  async function save(e) {
    e.preventDefault();
    if (!form.image) { toast.error("Banner image is required."); return; }
    if (!form.pages || form.pages.length === 0) { toast.error("Pick at least one page."); return; }
    try {
      if (editing === "new") {
        await api.post("/promo-banners", form);
        toast.success("Banner created.");
      } else {
        await api.put(`/promo-banners/${editing}`, form);
        toast.success("Banner saved.");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  }

  async function remove(b) {
    if (!window.confirm(`Delete banner "${b.title || b.id}"?`)) return;
    try {
      await api.delete(`/promo-banners/${b.id}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error("Delete failed.");
    }
  }

  function togglePage(pid) {
    const set = new Set(form.pages || []);
    if (pid === "*") {
      setForm({ ...form, pages: set.has("*") ? [] : ["*"] });
      return;
    }
    set.delete("*");
    if (set.has(pid)) set.delete(pid); else set.add(pid);
    setForm({ ...form, pages: Array.from(set) });
  }

  return (
    <div data-testid="banners-tab" className="space-y-6">
      <div className="bg-[#FDFBF7] rounded-3xl thick-gold p-6">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-serif text-2xl">Spotlight banners</h3>
            <p className="text-sm text-[#5C5547] mt-1 max-w-2xl">
              Show different banners on different pages. Each banner targets one or more pages.
              Pick "All pages" to show on every page. Lower "sort order" = shown first when multiple banners match.
            </p>
          </div>
          <button onClick={openNew} data-testid="banner-new"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--accent)] text-black text-sm font-semibold">
            <Plus className="w-4 h-4" /> New banner
          </button>
        </div>

        {banners.length === 0 ? (
          <div className="mt-8 text-center py-12 text-sm text-[#5C5547] border border-dashed border-[#EAE4D3] rounded-2xl">
            No banners yet. Click "New banner" to create your first one. <br />
            <span className="text-xs">(Your legacy single banner from Site Settings will still show on the homepage until you create a new one.)</span>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {banners.map((b) => (
              <div key={b.id} data-testid={`banner-card-${b.id}`}
                className="rounded-2xl border border-[#EAE4D3] bg-[#FDFBF7] overflow-hidden">
                {b.image ? (
                  <div className="h-32 w-full bg-cover bg-center" style={{ backgroundImage: `url(${b.image})` }} />
                ) : (
                  <div className="h-32 w-full bg-[#F5F2EA] flex items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-[#5C5547]" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-serif text-lg truncate">{b.title || <span className="italic text-[#5C5547]">Untitled</span>}</div>
                      <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5547] mt-0.5">{b.eyebrow}</div>
                    </div>
                    <div className={`text-[10px] px-2 py-0.5 rounded-full ${b.enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                      {b.enabled ? "Live" : "Off"}
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-[#5C5547] line-clamp-2">{b.subtitle}</div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {(b.pages || []).map((p) => (
                      <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F5F2EA] text-[#2C2924]">
                        {p === "*" ? "All pages" : p}
                      </span>
                    ))}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => openEdit(b)} data-testid={`banner-edit-${b.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2C2924] text-[#FDFBF7] text-xs">
                      <Edit2 className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button onClick={() => remove(b)} data-testid={`banner-delete-${b.id}`}
                      className="inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-red-50 text-red-700 text-xs">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <form onSubmit={save} data-testid="banner-form"
            className="bg-[#FDFBF7] rounded-3xl border border-[#EAE4D3] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 relative">
            <button type="button" onClick={() => setEditing(null)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#F5F2EA]"><X className="w-4 h-4" /></button>
            <h2 className="font-serif text-2xl mb-4">{editing === "new" ? "New banner" : "Edit banner"}</h2>

            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  data-testid="banner-form-enabled" />
                Banner is live (uncheck to hide without deleting)
              </label>

              <div>
                <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547] mb-2">Image</div>
                <ImageUpload
                  value={form.image}
                  onChange={(url) => setForm({ ...form, image: url })}
                  label="Upload banner image"
                  testid="banner-form-image-upload"
                />
                <input value={form.image || ""}
                  onChange={(e) => setForm({ ...form, image: e.target.value })}
                  placeholder="…or paste an image URL"
                  data-testid="banner-form-image"
                  className="w-full mt-2 px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              </div>

              <input value={form.eyebrow || ""} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })}
                placeholder="Eyebrow (e.g. JUST DROPPED)" data-testid="banner-form-eyebrow"
                className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />

              <input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Headline" data-testid="banner-form-title"
                className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />

              <input value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                placeholder="Subtitle (one line)" data-testid="banner-form-subtitle"
                className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={form.cta_label || ""} onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                  placeholder="Button label (e.g. Shop now)" data-testid="banner-form-cta-label"
                  className="px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
                <input value={form.cta_link || ""} onChange={(e) => setForm({ ...form, cta_link: e.target.value })}
                  placeholder="Button link (/shop or https://…)" data-testid="banner-form-cta-link"
                  className="px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547] mb-1">Text alignment</div>
                  <select value={form.align || "left"}
                    onChange={(e) => setForm({ ...form, align: e.target.value })}
                    data-testid="banner-form-align"
                    className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm">
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
                <div>
                  <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547] mb-1">Sort order</div>
                  <input type="number" value={form.sort_order ?? 0}
                    onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0", 10) })}
                    data-testid="banner-form-sort"
                    className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm"
                    placeholder="0" />
                </div>
              </div>

              <div>
                <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547] mb-2">Show on pages</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {PAGE_OPTIONS.map((opt) => (
                    <label key={opt.id} data-testid={`banner-form-page-${opt.id}`}
                      className={`flex items-center gap-2 text-xs px-3 py-2 rounded-xl border cursor-pointer ${
                        (form.pages || []).includes(opt.id)
                          ? "border-[var(--accent)] bg-[#FDFBF7]"
                          : "border-[#EAE4D3] bg-[#F5F2EA]"
                      }`}>
                      <input type="checkbox" className="accent-[var(--accent)]"
                        checked={(form.pages || []).includes(opt.id)}
                        onChange={() => togglePage(opt.id)} />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-[#5C5547] mt-2">Tick "All pages" to show everywhere. Otherwise tick each page you want it on.</p>
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => setEditing(null)}
                className="flex-1 py-3 rounded-full bg-[#F5F2EA] text-sm">Cancel</button>
              <button type="submit" data-testid="banner-form-save"
                className="flex-1 py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">Save banner</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
